import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { EFileService } from '../../efile.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import { DashboardPopupService } from '../dashboard-popup/dashboard-popup.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

/** Mode the per-row review-attachment modal opens in. */
type AttachmentModalMode = 'prepare' | 'upload' | 'view';

interface SubmissionListColumn {
  title: string;
  data: string;
  /** Optional download link type rendered against the cell value */
  downloadType?: 'manifest' | 'ack' | 'receipt' | 'zip' | 'ifa';
  /** When true the cell is rendered through `Date` formatting */
  isDate?: boolean;
}

/**
 * Migrated host for the legacy `submissions-dasboard.submission.submit`
 * + `.submit.history` states from `ui/app/components/efile/efile.js`.
 *
 * Legacy layout (see `submit.html` + EfileService.js `submissionListColumns`):
 *   - TEST / PRODUCTION radio
 *   - Submit / Get-Ack / Ping IRS buttons
 *   - "IRS Submissions Status" grid loaded from
 *     `loadJsonObject?action_code=81j7mv` (params: submission_id, sso_id)
 *
 * The submit.history grid is rendered inline here instead of mounting
 * a child <ui-view> like AngularJS did.
 */
@Component({
    selector: 'gtw-efile-submit-action',
    templateUrl: './submit-action.component.html',
    styleUrls: ['./submit-action.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SubmitActionComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Input() submission: any;

  /**
   * Defensive deep-link redirect (port of SubmitController L633-636).
   * Legacy did `$state.go('submissions-dasboard.submission.dashboard', ...)`
   * when the user lacked EDITOR access or the submission was Production
   * Accepted. The migrated sidebar already hides the menu item under the
   * same conditions, but a direct URL or a status flip after the page is
   * open would bypass that gate. We emit a sub-state key that the parent
   * (`submission-detail`) forwards to its `subStateChange` output so the
   * router can navigate to `/dashboard`.
   */
  @Output() requestSubStateNav = new EventEmitter<string>();

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  rowData: any = {};
  filterParams: any = {};
  private static readonly JCD_KEY_FEDERAL = 250;
  envType: 'TEST' | 'PRODUCTION' = 'TEST';
  submitting = false;
  pinging = false;
  acking = false;

  // ---------------------------------------------------------------------------
  // Legacy SubmitController flags (efile/efileElements/EfileElementsController.js
  // L689-744). Drive the conditional banners, radio visibility and Submit/Get-Ack
  // button toggle in the template.
  // ---------------------------------------------------------------------------
  /**
   * Mirrors legacy `vm.ENV_FOR_PROCESS_SPLIT = EfileFactory.ENV_FOR_PROCESS_SPLIT`
   * (EfileElementsController.js L637). Read from `EFileService` (the migrated
   * counterpart of `EfileFactory`) so the constant lives in one place — see
   * `EFileService.ENV_FOR_PROCESS_SPLIT`. Exposed as a getter so existing
   * template / component references (`this.ENV_FOR_PROCESS_SPLIT`,
   * `{{ ENV_FOR_PROCESS_SPLIT }}`) continue to work unchanged.
   */
  get ENV_FOR_PROCESS_SPLIT(): 'PRODUCTION' {
    return this.efileService.ENV_FOR_PROCESS_SPLIT;
  }

  // ---------------------------------------------------------------------------
  // Access guard constants (port of SubmitController L627, L633-636 +
  // EfileService.js `hasAccess` userMap L29-32).
  // ---------------------------------------------------------------------------
  /** Mirrors legacy `const PRODUCTION_ACCEPTED` (SubmitController L627). */
  private static readonly PRODUCTION_ACCEPTED = 'Production Accepted';
  /**
   * Role → numeric threshold map mirroring `EfileService.js` L27-L32.
   * `hasEditorAccess()` returns true when `user_type_key >= ROLE_MAP.EDITOR`.
   */
  private static readonly ROLE_MAP: Record<string, number> = {
    USER: 1, EDITOR: 2, ADMIN: 3, DEVELOPER: 4, SUPER_ADMIN: 5,
  };
  /** Server-config gate for showing the PRODUCTION radio
   *  (EfileElementsController.js L710). */
  canDoProdSubmit = false;
  /** True when the active submission is an Extension return (RETURN_TYPE
   *  contains "Extension"; EfileElementsController.js L712). */
  isExtension = false;
  /** True when at least one IRS submission has SUBMISSION_RECEIPT_EXISTS=No
   *  (EfileElementsController.js L1505 + L731 listener). */
  pendingSubmission = false;
  /** True when CRITICAL_DIAG_COUNT > 0 from action_code=787b9e
   *  (EfileElementsController.js L1516 + L738 listener). */
  pendingCriticalDiagnostics = false;
  /** True when the latest submission has ACKNOWLEDGEMENT_EXISTS=No AND
   *  SUBMISSION_RECEIPT_EXISTS=Yes (EfileElementsController.js L724 listener
   *  fed by L1512 emit). Hides the Submit button and shows Get Acknowledgment. */
  onlyGetAck = false;
  /** Legacy flag toggled by `get-ack-some` event; maintained for parity. */
  enableGetAck = false;

  // ---------------------------------------------------------------------------
  // IRS Submissions Status grid (port of EfileFactory.submissionListColumns +
  // EfileDashboardPopupController.loadData; see efile.js submit.history state).
  // ---------------------------------------------------------------------------
  readonly submissionColumns: SubmissionListColumn[] = [
    { title: 'Entity',                data: 'IRS_ID' },
    { title: 'Manifest File',         data: 'MANIFEST_EXISTS',          downloadType: 'manifest' },
    { title: 'Acknowledgement File',  data: 'ACKNOWLEDGEMENT_EXISTS',   downloadType: 'ack' },
    { title: 'Submission Receipt',    data: 'SUBMISSION_RECEIPT_EXISTS',downloadType: 'receipt' },
    { title: 'Submission Zip',        data: 'ZIP_FILE_NAME',            downloadType: 'zip' },
    { title: 'IFA File',              data: 'IFA_FILE',                 downloadType: 'ifa' },
    { title: 'Submission Indicator',  data: 'ENVIRONMENT' },
    { title: 'Accepted',              data: 'ACCEPTED' },
    { title: 'Submitted By',          data: 'CREATED_BY' },
    { title: 'Submitted Date',        data: 'CREATED_TS', isDate: true },
  ];

  submissions: any[] = [];
  filteredSubmissions: any[] = [];
  loadingSubmissions = false;
  submissionsError: string | null = null;
  searchTerm = '';

  // Sort state for the table headers. Same affordance as the PDF
  // upload table — the legacy gt-datatable2 also sorted in-memory.
  sortKey = '';
  sortDir: 'asc' | 'desc' = 'asc';

  // Confirm dialog for Reject / Mark-as-Reviewed actions + the PRODUCTION
  // two-step submit confirmation (legacy `openConfirmation`). Rendered through
  // the shared `<gtw-confirmation-modal>` to match every other efile screen
  // (return-review, scrubbing, efile-setup, key-xml-gen, ...).
  showConfirm = false;
  confirmConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
  };
  pendingAction: (() => void) | null = null;

  // ---------------------------------------------------------------------------
  // Send Return to IRS modal chain (legacy SubmitController L766-827).
  // Each click of the top-level Submit button walks:
  //   `prepare` upload modal (only for PRODUCTION non-extension when no
  //                            taxDocUrl has been captured yet)
  //   → `gtw-efile-req-header-elements` modal (action_code=yzgy3w)
  //   → for PRODUCTION: two sequential confirm dialogs
  //   → `EfileService.submitToIRS`.
  // ---------------------------------------------------------------------------

  /** Captured S3 file key of the most-recent prod-prep tax-doc upload.
   *  Mirrors `EfileFactory.taxDocUrl` (EfileService.js L21 + L669-680). */
  taxDocUrl = '';

  /** Toggles the embedded review-attachment overlay. */
  showAttachmentModal = false;
  attachmentModalMode: AttachmentModalMode = 'prepare';
  /** Always `upload-review-attachments.html` header copy (L3). */
  readonly attachmentModalTitle = 'Upload a signed Tax Doc File';
  attachmentModalIrsId = '';
  attachmentModalReadOnly = false;
  /**
   * Legacy `vm.efileReviewAttachmentUploadInput` JSON
   * (EfileElementsController.js L1207-1218).
   */
  reviewAttachmentUploadInputJson = '';

  /** Toggles the embedded req-header-elements overlay. */
  showReqHeaderModal = false;

  /** Per-row submit (legacy `vm.sendToIRS`) — tracked separately so the
   *  grid spinner doesn't flash while a top-level Submit is in flight. */
  rowSubmittingIrsId = '';

  constructor(
    private efileService: EFileService,
    private snackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private appService: AppService,
    private dashboardPopupService: DashboardPopupService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.loadSubmissionsHistory();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.loadSubmissionsHistory();
      })
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.loadSubmissionsHistory();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.efileService.setBaseUrls(this.baseURLs);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = this.submission && this.submission.SUBMISSION_ID
      ? this.submission
      : rowPayloadFromActiveScreen(activeScreen);

    // canDoProdSubmit / isExtension parity with SubmitController L710-712.
    const prodUsers =
      (this.appService as any)?.SERVER_CONFIG?.clientConfig?.efile_prod_submission_users;
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    this.canDoProdSubmit = Array.isArray(prodUsers)
      ? prodUsers.includes(ssoId)
      : typeof prodUsers === 'string'
        ? prodUsers.includes(ssoId)
        : false;
    const returnType = String(this.rowData?.RETURN_TYPE || '');
    this.isExtension = returnType.includes('Extension');

    // Defensive deep-link guard — port of SubmitController L633-636. The
    // sidebar already hides "Send Return to IRS" when these predicates
    // are true, but this catches:
    //   - direct URL navigation to `/.../submit`
    //   - a status flip to Production Accepted while the user is sitting
    //     on the submit tab (re-triggered every time globalParams /
    //     setTabActive fires).
    this.enforceAccessGuard();
  }

  /**
   * Mirrors `EfileFactory.hasAccess("EDITOR")` (EfileService.js L136):
   * `USER_SETTINGS.user.client.user_type_key >= ROLE_MAP.EDITOR`.
   */
  private hasEditorAccess(): boolean {
    const typeKey: number =
      this.appService.USER_SETTINGS?.user?.client?.user_type_key ?? 0;
    return typeKey >= (SubmitActionComponent.ROLE_MAP.EDITOR ?? 99);
  }

  /**
   * Emits a `'dashboard'` nav request to the parent (`submission-detail`)
   * when the user lacks EDITOR access or the submission has been
   * Production Accepted. Mirrors the legacy `$state.go(...dashboard)`
   * redirect at SubmitController L633-636.
   *
   * The check is skipped while the submission row is still hydrating
   * (`rowData` empty) so we don't bounce the user before the workspace
   * has finished resolving the active screen.
   */
  private enforceAccessGuard(): void {
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    const status = String(this.rowData?.SUBMISSION_STATUS || '');
    const blocked =
      !this.hasEditorAccess() ||
      status === SubmitActionComponent.PRODUCTION_ACCEPTED;
    if (blocked) {
      this.requestSubStateNav.emit('dashboard');
    }
  }

  // ---------------------------------------------------------------------------
  // Top-level actions (Submit / Get Ack / Ping IRS)
  // ---------------------------------------------------------------------------

  /**
   * Legacy SubmitController `showUploadTaxDocPopup` (L766-775).
   * Top-level Submit entry: decides whether the PRODUCTION tax-doc upload
   * modal needs to fire before opening the mandatory-header review modal.
   * Pure TEST submissions and Extension returns skip straight to the header
   * review modal (no upload required by IRS).
   */
  submit(): void {
    if (this.submitting || this.isSubmitDisabled) return;
    if (
      !this.isExtension &&
      String(this.envType).toLowerCase() === String(this.ENV_FOR_PROCESS_SPLIT).toLowerCase() &&
      !this.taxDocUrl
    ) {
      this.openAttachmentModal('prepare');
      return;
    }
    this.showReqHeaderElements();
  }

  /**
   * Opens the embedded `<gtw-efile-req-header-elements>` overlay
   * (port of legacy `openreqHeaderPopup`, EfileElementsController.js L786-866).
   * Once the user clicks Proceed inside the overlay, `onReqHeaderProceed`
   * decides whether to fire the PROD double-confirm or submit directly.
   */
  showReqHeaderElements(): void {
    this.showReqHeaderModal = true;
    this.cdr.markForCheck();
  }

  /** Called when the embedded req-header modal emits `proceed`. */
  onReqHeaderProceed(): void {
    this.showReqHeaderModal = false;
    this.cdr.markForCheck();
    if (String(this.envType).toUpperCase() === 'TEST') {
      this.doSubmit();
      return;
    }
    // Two sequential confirm dialogs for PRODUCTION (legacy
    // `openConfirmation` chains two `confirm-action.html` modals).
    this.openConfirm(
      'Are you sure you want to submit to IRS Prod?',
      () => {
        this.openConfirm(
          'Are you sure you want to submit to IRS Prod?',
          () => this.doSubmit(),
        );
      },
    );
  }

  /** Called when the embedded req-header modal emits `cancel`. */
  onReqHeaderCancel(): void {
    this.showReqHeaderModal = false;
    this.cdr.markForCheck();
  }

  /**
   * Final submission step — mirrors `doSubmit` in SubmitController L735-754.
   * For PRODUCTION + non-extension returns the legacy code surfaces a
   * different success message ("eFile Team is notified…") and queues a
   * `updateTaxDocUrl` follow-up after the grid reloads.
   */
  private doSubmit(): void {
    if (this.submitting) return;
    const subId = String(this.rowData?.SUBMISSION_ID || '');
    if (!subId) {
      this.snackbar.openSnackBar('No submission selected.', 'gtw-snackbar--red');
      return;
    }
    const shouldUpdateTaxDocUrl =
      !this.isExtension &&
      String(this.envType).toLowerCase() === String(this.ENV_FOR_PROCESS_SPLIT).toLowerCase();
    const successMessage = shouldUpdateTaxDocUrl
      ? 'eFile Team is notified of the newly created XML for Validation. The Submission will be transmitted to IRS post-validation.'
      : 'Successfully Submitted to IRS!';

    this.submitting = true;
    this.cdr.markForCheck();
    this.efileService.submitToIRS(0, this.envType, subId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.submitting = false;
        const ok = res?.callSuccess === '1';
        if (ok) {
          this.snackbar.openSnackBar(successMessage, 'gtw-snackbar--green');
          this.loadSubmissionsHistory(shouldUpdateTaxDocUrl);
        } else {
          const errMsg = res?.errorMessage
            ? `Failed to Submit to IRS!${res.errorMessage}`
            : 'Failed to Submit to IRS!';
          this.snackbar.openSnackBar(errMsg, 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.submitting = false;
        this.snackbar.openSnackBar('Failed to Submit to IRS!', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      },
    });
  }

  // --------------------------- attachment modal ----------------------------

  /**
   * Port of `vm.uploadReviewAttachments(irsId, isReadOnly)`
   * (EfileElementsController.js L1189-1237) + `$uibModal` template
   * `upload-review-attachments.html`.
   *
   * `mode`:
   *   - `prepare`: opened via `show-upload-tax-doc-popup` (no `irsId`).
   *   - `upload`: grid Upload link (`irsId` set).
   *   - `view`: grid View link (`irsId` + read-only).
   */
  openAttachmentModal(mode: AttachmentModalMode, irsId: string = ''): void {
    this.attachmentModalMode = mode;
    this.attachmentModalIrsId = irsId;
    this.attachmentModalReadOnly = mode === 'view';
    this.reviewAttachmentUploadInputJson = this.buildReviewAttachmentUploadInput(irsId);
    this.showAttachmentModal = true;
    this.cdr.markForCheck();
  }

  /**
   * Same payload as legacy `vm.efileReviewAttachmentUploadInput` (L1207-1218).
   */
  private buildReviewAttachmentUploadInput(irsId?: string): string {
    const client = this.gtwGlobal.getClient?.() || {};
    const submissionId = String(
      this.rowData?.SUBMISSION_ID ?? this.submission?.SUBMISSION_ID ?? '',
    );
    const payload: Record<string, unknown> = {
      clientKey: client.client_key ?? this.appService.USER_SETTINGS?.user?.client_key ?? '',
      baseUrls: {
        admin: this.baseURLs.admin,
        api: this.baseURLs.api,
      },
      submissionId,
      isProdSubmissionUser: this.canDoProdSubmit,
      title: 'Upload Signed Tax Doc File',
    };
    if (irsId) {
      payload.irsSubmissionId = irsId;
    }
    return JSON.stringify(payload);
  }

  closeAttachmentModal(): void {
    this.showAttachmentModal = false;
    this.cdr.markForCheck();
  }

  /**
   * `on-efile-review-attachment-upload` from the CE (only emitted when no
   * `irsSubmissionId` — prepare / tax-doc popup flow).
   *
   * - uploadReviewAttachments modal host (L1226-1228): `vm.close()`.
   * - SubmitController (L665-676): set `EfileFactory.taxDocUrl`, then
   *   `showReqHeaderEle()` — handled when `attachmentModalMode === 'prepare'`.
   */
  onAttachmentUploaded(payload: any): void {
    if (this.attachmentModalMode === 'prepare') {
      const callSuccess =
        payload?.callSuccess === '1' || payload?.callSuccess === 1;
      if (!callSuccess) {
        this.snackbar.openSnackBar(
          'Failed to upload the review attachment',
          'gtw-snackbar--red',
        );
        return;
      }
      const filePath = String(
        (payload && (payload.filePath || payload.file_path)) || '',
      );
      if (!filePath) {
        this.snackbar.openSnackBar(
          'Failed to upload the review attachment',
          'gtw-snackbar--red',
        );
        return;
      }
      const chunks = filePath.split('/');
      this.taxDocUrl = chunks[chunks.length - 1] || '';
      this.closeAttachmentModal();
      this.showReqHeaderElements();
      return;
    }

    // Legacy uploadReviewAttachments modal: close on upload event only (L1226-1228).
    this.closeAttachmentModal();
  }

  pingIRS(): void {
    if (this.pinging) return;
    this.pinging = true;
    this.efileService.pingIrsServer(0, this.envType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.pinging = false;
        this.snackbar.openSnackBar(
          res?.callSuccess === '1' ? 'Successfully Pinged IRS!' : 'Failed to Ping IRS!',
          res?.callSuccess === '1' ? 'gtw-snackbar--green' : 'gtw-snackbar--red'
        );
      },
      error: () => {
        this.pinging = false;
        this.snackbar.openSnackBar('Failed to Ping IRS!', 'gtw-snackbar--red');
      }
    });
  }

  getAck(): void {
    if (this.acking) return;
    this.acking = true;
    const { subId, taxYear, scenario, jcdKey } = this.buildPollAckParams();
    this.efileService.pollAck(0, subId, taxYear, scenario, jcdKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.acking = false;
        const ok = res?.callSuccess === '1';
        if (ok) {
          this.snackbar.openSnackBar(
            'Successfully Fetched Acknowledgment from IRS!',
            'gtw-snackbar--green'
          );
          this.enableGetAck = false;
        } else {
          this.snackbar.openSnackBar(
            String(res?.errorMessage || ''),
            'gtw-snackbar--yellow'
          );
        }
        this.loadSubmissionsHistory();
      },
      error: (error: any) => {
        this.acking = false;
        const errMsg = String(
          error?.error?.errorMessage || error?.errorMessage || ''
        );
        if (errMsg) {
          this.snackbar.openSnackBar(errMsg, 'gtw-snackbar--yellow');
        }
        this.loadSubmissionsHistory();
      }
    });
  }

  /**
   * Legacy EfileFactory.getAck parity (EfileService.js): GET /pollAck with
   * subId, taxYear, scenario, jcdKey — values from submission row / filters.
   */
  private buildPollAckParams(): { subId: string; taxYear: string; scenario: string; jcdKey: string } {
    const subId = String(this.rowData?.SUBMISSION_ID || '');
    const taxYear = String(
      this.rowData?.TAX_YEAR ?? this.filterParams?.tax_year ?? this.gtwGlobal.global?.globalParams?.tax_year ?? '',
    );
    const jcdKey = String(
      this.rowData?.JCD_KEY ??
        this.filterParams?.jcd_key ??
        this.gtwGlobal.global?.globalParams?.jcd_key ??
        SubmitActionComponent.JCD_KEY_FEDERAL,
    );
    const scenario = String(
      this.rowData?.SCENARIO ??
        this.rowData?.SCENARIO_ID ??
        this.filterParams?.scenario ??
        this.resolveDefaultScenario(taxYear) ??
        this.gtwGlobal.global?.globalParams?.scenario ??
        '',
    );
    return { subId, taxYear, scenario, jcdKey };
  }

  /** Mirrors SubmitController#getAck scenario lookup (EfileElementsController.js L914-917). */
  private resolveDefaultScenario(taxYear: string): string | number | undefined {
    const values: any[] = (this.gtwGlobal.global as any)?.inputs?.tax_year?.values || [];
    const ty = String(taxYear ?? '');
    const found = _.find(values, { label: ty }) || _.find(values, (v: any) => String(v?.value) === ty);
    const scenarios: any[] = found?.scenarios || [];
    const def = _.find(
      scenarios,
      (s: any) =>
        Number(s?.jcd_key) === SubmitActionComponent.JCD_KEY_FEDERAL && s?.default_scenario === 'Y',
    );
    return def?.scenario;
  }

  // ---------------------------------------------------------------------------
  // Submissions history grid
  // ---------------------------------------------------------------------------

  /**
   * Loads the IRS Submissions Status data via the same backend call the
   * legacy state used: `/loadJsonObject?action_code=81j7mv` with
   * `submission_id` + `sso_id`.
   *
   * @param shouldUpdateTaxDocUrl When true (set after a successful PROD
   *  non-extension `doSubmit`), the newly created IRS row's `IRS_ID` is
   *  paired with `taxDocUrl` and persisted via `updateTaxDocUrl`. Mirrors
   *  EfileElementsController.js L1338-1339.
   */
  loadSubmissionsHistory(shouldUpdateTaxDocUrl: boolean = false): void {
    const submissionId = String(this.rowData?.SUBMISSION_ID || '');
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const baseUrl = generalConfigFromGlobal(this.gtwGlobal).base_url || '/gtw';

    if (!submissionId) {
      this.submissions = [];
      this.filteredSubmissions = [];
      return;
    }

    this.loadingSubmissions = true;
    this.submissionsError = null;
    this.cdr.markForCheck();

    // Load critical-diagnostics flag in parallel — mirrors
    // EfileElementsController.js L1491 (action_code=787b9e), which
    // populates `pendingCriticalDiagnostics` from CRITICAL_DIAG_COUNT.
    this.dashboardPopupService
      .loadSubmissions(baseUrl, '/loadJsonObject?action_code=787b9e', {
        submission_id: submissionId,
        sso_id: ssoId,
      }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const rows = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
          const count = rows.length > 0 ? Number(rows[0]?.CRITICAL_DIAG_COUNT || 0) : 0;
          this.pendingCriticalDiagnostics = count > 0;
          this.cdr.markForCheck();
        },
        error: () => {
          this.pendingCriticalDiagnostics = false;
          this.cdr.markForCheck();
        },
      });

    this.dashboardPopupService
      .loadSubmissions(baseUrl, '/loadJsonObject?action_code=81j7mv', {
        submission_id: submissionId,
        sso_id: ssoId,
      }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.submissions = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
          this.recomputeSubmissionFlags();
          this.applyFilterAndSort();
          this.loadingSubmissions = false;
          this.maybeUpdateTaxDocUrl(shouldUpdateTaxDocUrl, baseUrl, ssoId);
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          console.error('[submit-action] loadSubmissionsHistory failed', err);
          this.submissions = [];
          this.filteredSubmissions = [];
          this.pendingSubmission = false;
          this.onlyGetAck = false;
          this.loadingSubmissions = false;
          this.submissionsError = 'Failed to load submission history';
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Legacy EfileElementsController.js L1338-1339 — pair the freshly
   * created IRS row with the previously uploaded tax-doc S3 key. The
   * legacy controller looked at the first row of the refreshed grid
   * (since `pendingSubmission` rows always sort to the top) and called
   * `EfileFactory.updateTaxDocUrl(IRS_ID)` which posts the same S3 file
   * key (`EfileFactory.taxDocUrl`) under action `6mtt1s`.
   */
  private maybeUpdateTaxDocUrl(
    shouldUpdate: boolean,
    baseUrl: string,
    ssoId: string,
  ): void {
    if (!shouldUpdate || !this.taxDocUrl) return;
    if (!this.pendingSubmission || this.submissions.length === 0) return;
    const irsId = String(this.submissions[0]?.IRS_ID || '');
    if (!irsId) return;
    const fileKey = this.taxDocUrl;
    // Clear immediately so a second submit click can't reuse the same file
    // (legacy behavior — efileFactory.taxDocUrl is cleared inside the
    // saveJSON callback in EfileService.js L677).
    this.taxDocUrl = '';
    this.efileService.updateTaxDocUrl(baseUrl, irsId, ssoId, fileKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (res?.callSuccess === '1') {
          this.snackbar.openSnackBar(
            'Successfully uploaded the review attachment',
            'gtw-snackbar--green',
          );
        }
      },
      error: () => {
        console.error('[submit-action] updateTaxDocUrl failed');
      },
    });
  }

  /**
   * Port of the `loadSubmissions()` post-processing block in
   * EfileElementsController.js L1499-1513:
   *   - `pendingSubmission`  ← any row with SUBMISSION_RECEIPT_EXISTS === 'No'
   *   - `onlyGetAck`         ← latest row has ACKNOWLEDGEMENT_EXISTS === 'No'
   *                            AND SUBMISSION_RECEIPT_EXISTS === 'Yes'
   */
  private recomputeSubmissionFlags(): void {
    const rows = this.submissions || [];
    if (rows.length === 0) {
      this.pendingSubmission = false;
      this.onlyGetAck = false;
      return;
    }

    this.pendingSubmission = rows.some(
      (row: any) => String(row?.SUBMISSION_RECEIPT_EXISTS) === 'No',
    );

    const first = rows[0] || {};
    this.onlyGetAck =
      String(first.ACKNOWLEDGEMENT_EXISTS) === 'No' &&
      String(first.SUBMISSION_RECEIPT_EXISTS) === 'Yes';
  }

  /**
   * Submit button label parity with submit.html L127:
   *   `envType === ENV_FOR_PROCESS_SPLIT && !isExtension ? "Prepare Submission" : "Submit"`
   */
  get submitButtonLabel(): string {
    return this.envType === this.ENV_FOR_PROCESS_SPLIT && !this.isExtension
      ? 'Prepare Submission'
      : 'Submit';
  }

  /** Disabled state parity with submit.html L122. */
  get isSubmitDisabled(): boolean {
    return this.submitting || this.pendingSubmission || this.pendingCriticalDiagnostics;
  }

  refreshHistory(): void {
    this.loadSubmissionsHistory();
  }

  printHistory(): void {
    const rows = this.filteredSubmissions || [];
    const headers = this.submissionColumns.map((col) => `<th>${this.escapeHtml(col.title)}</th>`).join('');
    const body = rows.map((row) => {
      const cells = this.submissionColumns
        .map((col) => `<td>${this.escapeHtml(this.formatCell(row, col))}</td>`)
        .join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    const printWindow = window.open('', '_blank', 'toolbar=no,location=no,menubar=no,scrollbars=yes,resizable=yes,width=900,height=700');
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>IRS Submissions Status</title>
          <style>
            table { width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
            th, td { border: 1px solid #999; padding: 6px 8px; text-align: left; }
            th { background: #939393; color: #fff; }
          </style>
        </head>
        <body>
          <table>
            <thead><tr>${headers}</tr></thead>
            <tbody>${body || `<tr><td colspan="${this.submissionColumns.length}">No submission history available.</td></tr>`}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  exportHistoryToExcel(): void {
    const rows = this.filteredSubmissions || [];
    const csvRows = [
      this.submissionColumns.map((col) => this.csvCell(col.title)).join(','),
      ...rows.map((row) =>
        this.submissionColumns.map((col) => this.csvCell(this.formatCell(row, col))).join(','),
      ),
    ];
    const blob = new Blob([`\ufeff${csvRows.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `irs-submissions-status-${this.rowData?.SUBMISSION_ID || 'export'}.csv`;
    link.dispatchEvent(new MouseEvent('click'));
    setTimeout(() => URL.revokeObjectURL(href), 0);
  }

  // --------------------------- search + sort -------------------------------

  onSearchChange(value: string): void {
    this.searchTerm = value || '';
    this.applyFilterAndSort();
  }

  toggleSort(key: string): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.applyFilterAndSort();
  }

  sortIconClass(key: string): string {
    if (this.sortKey !== key) return 'fa fa-sort';
    return this.sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
  }

  private applyFilterAndSort(): void {
    const term = (this.searchTerm || '').toLowerCase().trim();
    let rows = [...this.submissions];
    if (term) {
      rows = rows.filter((row: any) =>
        this.submissionColumns.some((col) => {
          const v = row?.[col.data];
          return v != null && String(v).toLowerCase().includes(term);
        }),
      );
    }
    if (this.sortKey) {
      const dir = this.sortDir === 'asc' ? 1 : -1;
      rows.sort((a: any, b: any) => {
        const av = a?.[this.sortKey];
        const bv = b?.[this.sortKey];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * dir;
        if (bv == null) return 1 * dir;
        if (this.sortKey === 'CREATED_TS') {
          const ad = Date.parse(av);
          const bd = Date.parse(bv);
          if (!isNaN(ad) && !isNaN(bd)) return (ad - bd) * dir;
        }
        return String(av).localeCompare(String(bv)) * dir;
      });
    }
    this.filteredSubmissions = rows;
    this.cdr.markForCheck();
  }

  // --------------------------- row helpers ---------------------------------

  formatCell(row: any, col: SubmissionListColumn): string {
    const v = row?.[col.data];
    if (v == null) return '';
    if (col.isDate) {
      return this.formatSubmittedTimestamp(v);
    }
    return String(v);
  }

  /** e.g. Jan 16, 2025 12:40:21 AM */
  private formatSubmittedTimestamp(value: any): string {
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return String(value ?? '');
    }
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = months[d.getMonth()];
    const day = d.getDate();
    const year = d.getFullYear();
    let h24 = d.getHours();
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    let h12 = h24 % 12;
    if (h12 === 0) {
      h12 = 12;
    }
    const mi = String(d.getMinutes()).padStart(2, '0');
    const se = String(d.getSeconds()).padStart(2, '0');
    return `${month} ${day}, ${year} ${h12}:${mi}:${se} ${ampm}`;
  }

  isAckRed(col: SubmissionListColumn, value: any): boolean {
    return col.downloadType === 'ack' && value === 'No';
  }

  // --------------------------- actions -------------------------------------

  /**
   * Port of `vm.downloadFile` in EfileElementsController.js L1268-1299.
   * The legacy code picks a per-type extension + MIME hint and warns when
   * the response payload is empty. Naming convention from legacy:
   *   - manifest / receipt → `${type}_${irsId}.xml`  (application/xml)
   *   - ack                → `ack_${irsId}.pdf`      (application/pdf)
   *   - ifa                → `ifa_${irsId}.tf`       (application/tf)
   *   - zip / fallback     → `${irsId}.zip`          (application/zip)
   *     (note: legacy intentionally drops the prefix for zip)
   */
  downloadFile(type: string, irsId: string): void {
    if (!irsId) return;
    const efileBase = this.baseURLs?.efile || '';
    this.dashboardPopupService.downloadFile(efileBase, irsId, type).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (buffer: ArrayBuffer) => {
        if (!buffer || buffer.byteLength === 0) {
          this.snackbar.openSnackBar('No file available to download', 'gtw-snackbar--yellow');
          return;
        }
        const { ext, prefix } = this.resolveDownloadNaming(type);
        const blob = new Blob([buffer], { type: `application/${ext}` });
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.download = `${prefix}${irsId}.${ext}`;
        link.dispatchEvent(new MouseEvent('click'));
        setTimeout(() => URL.revokeObjectURL(href), 0);
      },
      error: () => {
        this.snackbar.openSnackBar('Failed to download file', 'gtw-snackbar--red');
      },
    });
  }

  /**
   * Mirrors the legacy `switch(type)` block in `vm.downloadFile` —
   * keeps the prefix + extension mapping co-located so the filename
   * is identical to what AngularJS produced.
   */
  private resolveDownloadNaming(type: string): { ext: string; prefix: string } {
    switch (type) {
      case 'manifest':
      case 'receipt':
        return { ext: 'xml', prefix: `${type}_` };
      case 'ack':
        return { ext: 'pdf', prefix: 'ack_' };
      case 'ifa':
        return { ext: 'tf', prefix: 'ifa_' };
      default:
        // Legacy "zip" / fallback branch — no prefix, just `<irsId>.zip`.
        return { ext: 'zip', prefix: '' };
    }
  }

  markAsReviewed(irsId: string): void {
    this.openConfirm(
      'Are you sure you want to mark this submission as reviewed?',
      () => {
        const baseUrl = generalConfigFromGlobal(this.gtwGlobal).base_url || '/gtw';
        this.dashboardPopupService.markAsReviewed(baseUrl, irsId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (res: any) => {
            const ok = res?.callSuccess === '1';
            this.snackbar.openSnackBar(
              ok ? 'Submission marked as reviewed' : 'Failed to mark submission as reviewed',
              ok ? 'gtw-snackbar--green' : 'gtw-snackbar--red',
            );
            if (ok) this.loadSubmissionsHistory();
          },
          error: () => this.snackbar.openSnackBar('Failed to mark submission as reviewed', 'gtw-snackbar--red'),
        });
      },
    );
  }

  // ---------------------------------------------------------------------------
  // IRS Submissions Status grid actions — port of EfileService.js
  // `renderActions` (L109-134). The Actions column is rendered only for
  // PRODUCTION rows; visibility of each action follows the legacy state
  // machine driven by `canDoProdSubmit`, `SUBMISSION_RECEIPT_EXISTS`, and
  // `IS_REVIEWED`.
  // ---------------------------------------------------------------------------

  /** True when an action column should render for this row. */
  hasRowActions(row: any): boolean {
    return (
      String(row?.ENVIRONMENT || '').toLowerCase() ===
      String(this.ENV_FOR_PROCESS_SPLIT).toLowerCase()
    );
  }

  /** View link visible when the user lacks prod-submit access. */
  showView(row: any): boolean {
    return this.hasRowActions(row) && !this.canDoProdSubmit;
  }

  /** Upload link visible for prod-submit users on PRODUCTION rows. */
  showUpload(row: any): boolean {
    return this.hasRowActions(row) && this.canDoProdSubmit;
  }

  /**
   * "Complete Review" / "Reject" appear only while the submission has not
   * been transmitted yet (SUBMISSION_RECEIPT_EXISTS !== 'Yes') AND it has
   * not been marked reviewed yet (IS_REVIEWED !== 'Y'). Mirrors the
   * `renderActions` branch at EfileService.js L133.
   */
  showReviewActions(row: any): boolean {
    if (!this.hasRowActions(row) || !this.canDoProdSubmit) return false;
    return (
      String(row?.SUBMISSION_RECEIPT_EXISTS) !== 'Yes' &&
      String(row?.IS_REVIEWED) !== 'Y'
    );
  }

  /**
   * "Send To IRS" appears once the row has been reviewed but not yet
   * transmitted (IS_REVIEWED === 'Y' AND SUBMISSION_RECEIPT_EXISTS !== 'Yes').
   * Mirrors the `renderActions` branch at EfileService.js L129-131.
   */
  showSendToIRS(row: any): boolean {
    if (!this.hasRowActions(row) || !this.canDoProdSubmit) return false;
    return (
      String(row?.IS_REVIEWED) === 'Y' &&
      String(row?.SUBMISSION_RECEIPT_EXISTS) !== 'Yes'
    );
  }

  onRowView(row: any): void {
    this.openAttachmentModal('view', String(row?.IRS_ID || ''));
  }

  onRowUpload(row: any): void {
    this.openAttachmentModal('upload', String(row?.IRS_ID || ''));
  }

  /**
   * Legacy `vm.sendToIRS` + `vm.submitValidReturn` (EfileElementsController.js
   * L724-726 + EfileService.js L270-279). Asks the user to confirm twice
   * (matching `openConfirmation`) then calls `/submitValidatedReturn`.
   */
  onRowSendToIRS(row: any): void {
    const irsId = String(row?.IRS_ID || '');
    const env = String(row?.ENVIRONMENT || this.envType);
    const subId = String(this.rowData?.SUBMISSION_ID || '');
    if (!irsId || !subId) return;
    if (this.rowSubmittingIrsId) return;
    this.openConfirm(
      'Are you sure you want to submit to IRS Prod?',
      () => {
        this.openConfirm(
          'Are you sure you want to submit to IRS Prod?',
          () => this.doSubmitValidatedReturn(env, subId, irsId),
        );
      },
    );
  }

  private doSubmitValidatedReturn(env: string, subId: string, irsId: string): void {
    this.rowSubmittingIrsId = irsId;
    this.cdr.markForCheck();
    this.efileService.submitValidatedReturn(env, subId, irsId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.rowSubmittingIrsId = '';
        const ok = res?.callSuccess === '1';
        if (ok) {
          this.snackbar.openSnackBar('Successfully Submitted to IRS!', 'gtw-snackbar--green');
          this.loadSubmissionsHistory();
        } else {
          const errMsg = res?.errorMessage
            ? `Failed to Submit to IRS!${res.errorMessage}`
            : 'Failed to Submit to IRS!';
          this.snackbar.openSnackBar(errMsg, 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.rowSubmittingIrsId = '';
        this.snackbar.openSnackBar('Failed to Submit to IRS!', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      },
    });
  }

  isRowSubmitting(row: any): boolean {
    return this.rowSubmittingIrsId === String(row?.IRS_ID || '');
  }

  discardReturn(irsId: string): void {
    this.openConfirm(
      'Are you sure you want to discard this return?',
      () => {
        const efileBase = this.baseURLs?.efile || '';
        const submissionId = String(this.rowData?.SUBMISSION_ID || '');
        this.dashboardPopupService.discardReturn(efileBase, submissionId, irsId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.snackbar.openSnackBar('Submission discarded', 'gtw-snackbar--green');
            this.loadSubmissionsHistory();
          },
          error: () => this.snackbar.openSnackBar('Failed to discard submission', 'gtw-snackbar--red'),
        });
      },
    );
  }

  // --------------------------- confirm modal -------------------------------

  private openConfirm(message: string, action: () => void): void {
    // Only the message changes per call — preserve title / icon / button copy
    // from the defaults so every confirm in this screen looks the same as the
    // rest of the efile flows.
    this.confirmConfig = { ...this.confirmConfig, message };
    this.pendingAction = action;
    this.showConfirm = true;
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    const action = this.pendingAction;
    this.showConfirm = false;
    this.pendingAction = null;
    this.cdr.markForCheck();
    if (action) action();
  }

  onCancelConfirm(): void {
    this.showConfirm = false;
    this.pendingAction = null;
    this.cdr.markForCheck();
  }

  trackByIrs(_i: number, row: any): string {
    return row?.IRS_ID ?? `${_i}`;
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private csvCell(value: any): string {
    return `"${String(value ?? '').replace(/"/g, '""')}"`;
  }
}
